import type { ConversationKind } from "@/ai-chat/sseClient";
import { isHcicDemoEnv } from "@/preview-mode/hcic-demo";
import {
  PREVIEW_INSURANCE_DROP_ITEMS,
  PREVIEW_MOBILE_ABANDON_ITEMS,
} from "@/preview-mode/session-replay/recordings";

import { getHcicResponseForPrompt } from "./hcic-responses";

// Subset of backend BlockKinds (emitter.go) that the canned responses use.
// `text` is always streamed as text_delta + text_done; the rest are sent as
// single `block` events. Restricting to a finite union (instead of accepting
// any string) lets TypeScript narrow on `kind === "text"` in the simulator.
export type StructuredBlockKind =
  | "kpis"
  | "funnel"
  | "table"
  | "chart"
  | "explore"
  | "suggestions"
  | "survey"
  | "survey_card"
  | "session_replay"
  | "thinking";

export type FakeBlock =
  | { kind: "text"; text: string }
  | { kind: StructuredBlockKind; data: unknown };

const PREVIEW_DISCLAIMER =
  "This is preview mode, so I can't actually answer this question against your real data. " +
  "Exit preview mode (top of the page) to send real questions to Freshpaint AI. " +
  "In the meantime, try one of the suggestions below — they have canned example responses you can explore.";

// Capped at 3: the SSE block schema (SuggestionsBlockSchema) rejects a
// suggestions block with more than 3 entries, and parseSSEBlock drops the whole
// block on a failed parse — so a 4th suggestion silently hid all of them. The
// session-replay followup stays in the set so those cards remain discoverable
// from the fallback disclaimer.
export const PREVIEW_SUGGESTED_FOLLOWUPS: string[] = [
  "What's my biggest conversion opportunity right now?",
  "Why is there a large drop-off in the appointment booking funnel?",
  "Show me recordings of people who dropped off at insurance verification.",
];

const WEEKLY_SUMMARY_BLOCKS: FakeBlock[] = [
  {
    kind: "text",
    text: "Here's the weekly performance summary for Meridian Health System.",
  },
  {
    kind: "kpis",
    data: {
      title: "Key metrics",
      items: [
        {
          label: "Total sessions",
          value: "12,480",
          trend: "+6.2%",
          trendDir: "up",
          sub: "vs. prior week",
        },
        {
          label: "Conversion rate",
          value: "4.2%",
          trend: "-0.4 pts",
          trendDir: "down",
          sub: "vs. prior week",
        },
        {
          label: "Top segment CVR",
          value: "9.8%",
          trend: "+1.1 pts",
          trendDir: "up",
          sub: "Returning visitors, mobile",
        },
        {
          label: "Worst funnel drop-off",
          value: "62%",
          trend: null,
          trendDir: "down",
          sub: "Insurance verification → Confirmation",
        },
      ],
    },
  },
  {
    kind: "text",
    text: "**Biggest opportunity this week:** the appointment booking funnel is leaking 62% of users between the insurance verification and confirmation steps. Recovering even a third of those would add ~190 booked appointments per week.",
  },
  {
    kind: "funnel",
    data: {
      title: "Appointment booking funnel",
      steps: [
        {
          label: "Visit booking page",
          value: 4820,
          urlPatterns: ["/book*"],
        },
        {
          label: "Choose service",
          value: 3145,
          urlPatterns: ["/book/service*"],
        },
        {
          label: "Choose provider & time",
          value: 2410,
          urlPatterns: ["/book/provider*"],
        },
        {
          label: "Insurance verification",
          value: 1380,
          urlPatterns: ["/book/insurance*"],
        },
        {
          label: "Confirm appointment",
          value: 524,
          urlPatterns: ["/book/confirm*"],
        },
      ],
      converted: 524,
    },
  },
  {
    kind: "text",
    text: "**Top 3 things to tackle this week:**\n\n1. **Insurance verification UX** — 62% drop-off here, the worst step in any funnel.\n2. **Cardiology service detail page** — 14% bounce rate (vs. 6% site avg) and the highest exit rate of any service line page.\n3. **Mobile booking flow** — mobile users convert at 2.9% vs. 5.6% on desktop, a 48% gap that's been widening for 3 weeks.",
  },
  {
    kind: "table",
    data: {
      title: "Top pages by sessions",
      columns: [
        { key: "path", label: "Path" },
        { key: "sessions", label: "Sessions" },
        { key: "conversions", label: "Conversions" },
        { key: "cvr", label: "CVR" },
      ],
      rows: [
        ["/", 3120, 198, "6.3%"],
        ["/services/cardiology", 1840, 64, "3.5%"],
        ["/locations/chicago", 1490, 72, "4.8%"],
        ["/book", 4820, 524, "10.9%"],
        ["/services/orthopedics", 1210, 54, "4.5%"],
      ],
    },
  },
  {
    kind: "suggestions",
    data: {
      suggestions: [
        "Why is there a large drop-off in the appointment booking funnel?",
        "How does mobile traffic compare to desktop in terms of conversions?",
        "Which pages should I prioritize for optimization?",
      ],
    },
  },
];

const FUNNEL_DIG_DEEPER_BLOCKS: FakeBlock[] = [
  {
    kind: "text",
    text: "Looking at the **Appointment Booking** funnel, the bottleneck is concentrated at the **Insurance verification → Confirmation** step. 62% of users who reach insurance verification never confirm — that's roughly 856 lost bookings per week, far more than any other step.",
  },
  {
    kind: "funnel",
    data: {
      title: "Appointment booking funnel",
      steps: [
        {
          label: "Visit booking page",
          value: 4820,
          urlPatterns: ["/book*"],
        },
        {
          label: "Choose service",
          value: 3145,
          urlPatterns: ["/book/service*"],
        },
        {
          label: "Choose provider & time",
          value: 2410,
          urlPatterns: ["/book/provider*"],
        },
        {
          label: "Insurance verification",
          value: 1380,
          urlPatterns: ["/book/insurance*"],
        },
        {
          label: "Confirm appointment",
          value: 524,
          urlPatterns: ["/book/confirm*"],
        },
      ],
      converted: 524,
    },
  },
  {
    kind: "text",
    text: "**Why users drop off at insurance verification:**\n\n1. **Member ID friction** — 41% of users mistype their member ID on the first try. The current validation rejects the entry without explaining the format.\n2. **Group number is unclear** — 28% of users leave it blank because they can't find it on their card. There's no inline help or \"I don't have a group number\" affordance.\n3. **Mobile keyboard mismatch** — the member-ID field uses a generic keyboard on mobile, so users hit the autocomplete bar and submit half-typed values.",
  },
  {
    kind: "kpis",
    data: {
      title: "Bottleneck-step diagnostics",
      items: [
        {
          label: "Insurance step CVR",
          value: "38%",
          trend: "-4 pts",
          trendDir: "down",
          sub: "vs. 30-day avg",
        },
        {
          label: "Member ID retry rate",
          value: "41%",
          trend: null,
          trendDir: "down",
          sub: "first-try error rate",
        },
        {
          label: "Group number blank rate",
          value: "28%",
          trend: null,
          trendDir: "down",
          sub: "mostly mobile",
        },
        {
          label: "Mobile-only abandons",
          value: "71%",
          trend: "+3 pts",
          trendDir: "down",
          sub: "of insurance-step exits",
        },
      ],
    },
  },
  {
    kind: "text",
    text: '**Suggested quick wins:**\n\n- Add inline format examples ("e.g., MERID-123456789") and validate as the user types instead of on submit.\n- Add an "I don\'t have my insurance card" path that lets users continue and finish verification later.\n- Set `inputmode="numeric"` on the member-ID field so mobile users get the right keyboard.\n\nA short on-page survey targeted to the insurance step would also confirm which copy and validation changes would have the biggest impact.',
  },
  {
    kind: "suggestions",
    data: {
      suggestions: [
        "Create a survey to ask users about insurance form difficulty.",
        "How does mobile traffic compare to desktop in terms of conversions?",
        "Which pages should I prioritize for optimization?",
      ],
    },
  },
];

// Single-recording analysis seeded by the playback page's "Analyze with AI"
// CTA. The simulator dispatches purely on conversationKind, so the canned
// response stays tonally generic ("the recording you shared") and leans on
// the dominant Meridian Health narrative — insurance-verification friction —
// since that's the funnel bottleneck the rest of preview mode tells the
// story around.
const SESSION_REPLAY_RECORDING_ANALYSIS_BLOCKS: FakeBlock[] = [
  {
    kind: "text",
    text: "I pulled the recording and walked through the visitor's session. Three friction points and one habit pattern explain the abandon.",
  },
  {
    kind: "kpis",
    data: {
      title: "Session at a glance",
      items: [
        {
          label: "Session duration",
          value: "4m 18s",
          trend: null,
          trendDir: "up",
          sub: "above 30-day median",
        },
        {
          label: "Pages visited",
          value: "5",
          trend: null,
          trendDir: "up",
          sub: "/services \u2192 /book \u2192 /book/insurance",
        },
        {
          label: "Form fields touched",
          value: "8",
          trend: null,
          trendDir: "down",
          sub: "all on the insurance step",
        },
        {
          label: "Outcome",
          value: "Abandoned",
          trend: null,
          trendDir: "down",
          sub: "exit on /book/insurance",
        },
      ],
    },
  },
  {
    kind: "text",
    text: "**What this visitor did:**\n\n1. Spent **47 seconds** on `/services/cardiology` reading two provider bios, then jumped straight to `/book` — a strong intent signal.\n2. Moved through service and provider selection in **22 seconds combined**; they already knew what they wanted.\n3. Hit `/book/insurance` and slowed down. The recording shows **three retry attempts** on the member-ID field, rejected each time without an inline format hint.\n4. Opened the group-number field, looked at it for 8 seconds, then left it blank.\n5. Tabbed to submit, paused for 12 seconds, and closed the tab.",
  },
  {
    kind: "text",
    text: "**Why this isn't a one-off:** the shape matches the dominant abandonment pattern at the insurance step site-wide (62% drop-off, 41% first-try member-ID error rate). This visitor was high-intent and got stuck on a form, not on a decision.",
  },
  {
    kind: "suggestions",
    data: {
      suggestions: [
        "Show me recordings of people who dropped off at insurance verification.",
        "Why is there a large drop-off in the appointment booking funnel?",
        "Create a survey to ask users about insurance form difficulty.",
      ],
    },
  },
];

// List analysis seeded by the recordings list "Analyze with AI" CTA. Same
// narrative spine as the single-recording response, but the framing flips
// from one visitor's story to the patterns-across-the-cohort that the list
// view is best at surfacing.
const SESSION_REPLAY_LIST_ANALYSIS_BLOCKS: FakeBlock[] = [
  {
    kind: "text",
    text: "I went through the recordings in this view and pulled out the patterns that show up across the cohort, not just in any one session.",
  },
  {
    kind: "kpis",
    data: {
      title: "Across these recordings",
      items: [
        {
          label: "Common exit page",
          value: "/book/insurance",
          trend: null,
          trendDir: "down",
          sub: "in 64% of sessions",
        },
        {
          label: "Time on step before exit",
          value: "1m 52s",
          trend: null,
          trendDir: "down",
          sub: "much higher than other steps",
        },
        {
          label: "Mobile share",
          value: "71%",
          trend: null,
          trendDir: "down",
          sub: "vs. 48% site-wide",
        },
        {
          label: "Returning visitors",
          value: "54%",
          trend: null,
          trendDir: "up",
          sub: "high-intent cohort",
        },
      ],
    },
  },
  {
    kind: "text",
    text: "**Three repeating patterns:**\n\n1. **Member-ID retries cluster on mobile.** 41% of mobile sessions in this cohort retry the member-ID field at least twice. The mobile keyboard defaults to autocomplete, which submits half-typed values.\n2. **Group-number field is skipped, not filled.** 28% of sessions tab past it, hit submit, then leave when validation rejects the form.\n3. **The exit clusters at the 90\u2013120s mark on insurance.** Visitors don't bounce immediately — they try, retry, then quit. That's a fixable UX issue, not a wrong-audience issue.",
  },
  {
    kind: "table",
    data: {
      title: "Top exit pages in this cohort",
      columns: [
        { key: "path", label: "Path" },
        { key: "sessions", label: "Sessions" },
        { key: "share", label: "Share" },
      ],
      rows: [
        ["/book/insurance", 47, "64%"],
        ["/book/provider", 11, "15%"],
        ["/services/cardiology", 8, "11%"],
        ["other", 7, "10%"],
      ],
    },
  },
  {
    kind: "text",
    text: "**Suggested next steps:**\n\n- Add inline format examples on the member-ID field and validate as the user types.\n- Add an \"I don't have my insurance card\" path so users aren't blocked by a missing group number.\n- Run a short on-page survey to confirm whether the friction is the form, the language, or trust.",
  },
  {
    kind: "suggestions",
    data: {
      suggestions: [
        "Show me recordings of mobile users who abandoned the booking flow.",
        "Why is there a large drop-off in the appointment booking funnel?",
        "Create a survey to ask users about insurance form difficulty.",
      ],
    },
  },
];

const RESPONSES_BY_PROMPT = new Map<string, FakeBlock[]>([
  [
    "What are we doing well with SEO that would help with our paid search strategy?",
    [
      {
        kind: "text",
        text: "Short answer: your **search engine optimization (SEO)** is doing a lot of the *closing*, and that's the part most relevant to your paid search strategy. From Jul 16 – Aug 15, 2026, **Organic Search** earned **7,118 conversions under last-touch attribution** — the model that credits only the final click before a conversion. Under a **linear (multi-touch)** model, which splits credit across every touch in the journey, it earns **5,306**. Organic getting **34% more credit as the last touch than as an average touch** tells you it's frequently the channel that *closes* the deal, not just one that introduces it.",
      },
      {
        kind: "kpis",
        data: {
          title: "Organic Search — Jul 16 – Aug 15, 2026",
          items: [
            {
              label: "Last-touch conversions",
              value: "7,118",
              trend: "+34% vs. linear",
              trendDir: "up",
              sub: "final-click credit",
            },
            {
              label: "Linear (multi-touch)",
              value: "5,306",
              trend: null,
              trendDir: "neutral",
              sub: "credit across all touches",
            },
            {
              label: "Share of conversions",
              value: "38%",
              trend: null,
              trendDir: "neutral",
              sub: "vs. Google Ads 44% (last-touch)",
            },
            {
              label: "Media cost",
              value: "$0",
              trend: null,
              trendDir: "neutral",
              sub: "vs. paid search spend",
            },
          ],
        },
      },
      {
        kind: "text",
        text: "**Why this matters for paid search:** organic is delivering roughly **59% of your paid search conversion volume at zero media cost**. When a channel you don't pay for is closing conversions at nearly the scale of the one you do, the highest-leverage question isn't \"how do I get more organic?\" — it's \"where am I *paying* for search intent I'm already winning for free?\"",
      },
      {
        kind: "chart",
        data: {
          chartType: "bar",
          title: "Share of conversions by channel — last-touch",
          subtitle:
            "Organic Search (38%) is nearly on par with Google Ads (44%).",
          dataSource:
            "Session-level data with marketing attribution, Jul 16 – Aug 15, 2026",
          xAxisLabel: "Channel",
          yAxisLabel: "Share of conversions (%)",
          series: [
            {
              name: "Share of conversions",
              data: [
                { label: "Google Ads", value: 44 },
                { label: "Organic Search", value: 38 },
                { label: "Direct", value: 8 },
                { label: "Email", value: 4 },
                { label: "Bing Ads", value: 2 },
                { label: "Referral", value: 2 },
                { label: "Display", value: 1 },
                { label: "Paid Social", value: 1 },
              ],
            },
          ],
        },
      },
      {
        kind: "thinking",
        data: {
          summary: "Sampling the content of your top organic landing pages",
        },
      },
      {
        kind: "text",
        text: '**What the pages themselves are doing well.** I pulled the content of two of your top organic-converting pages — `/services/cardiology` and `/services/orthopedics` — to see *why* they earn organic traffic. Both are structured the way patients and search engines reward:\n\n- **Intent-matched headings** — a clear top-level heading ("Cardiology Services") with sub-headings that mirror how patients search: *Conditions We Treat*, *Our Cardiologists*, *What to Expect*, *Book an Appointment*.\n- **Terminology patients actually type** — the copy names specific conditions and procedures (arrhythmia, echocardiogram) alongside the Chicago-area locations, so each page is relevant to both the clinical query and the "near me" query.\n- **Descriptive image alt text** — the provider and facility images carry meaningful alt text, which supports both accessibility and image search.\n\nThat on-page foundation is a big part of why these pages close organic conversions — and it\'s exactly the intent you\'re *also* buying on paid search.',
      },
      {
        kind: "text",
        text: "**One note on scope.** Reading the page content tells me *how* these pages are built, and the attribution data tells me how organic performs as a *channel* — its conversions, the pages it lands on, and where it sits in the buyer journey. What neither shows me is which individual keywords you rank for or your exact positions on the results page — that lives in your search console and ad platform. So I can confirm organic is carrying real closing volume on well-built pages and point to where it most likely overlaps with paid, but the exact keyword-by-keyword overlap means lining up the pages below against your Google Ads keyword list.",
      },
      {
        kind: "table",
        data: {
          title: "Where organic and paid search compete for the same pages",
          columns: [
            { key: "page", label: "Page" },
            { key: "organicSessions", label: "Organic sessions" },
            { key: "organicCvr", label: "Organic CVR" },
            { key: "paidSessions", label: "Paid search sessions" },
            { key: "overlap", label: "Overlap signal" },
          ],
          rows: [
            ["/services/cardiology", "4,210", "5.8%", "3,900", "High"],
            ["/services/orthopedics", "3,120", "5.1%", "2,650", "High"],
            ["/services/dermatology", "1,540", "4.0%", "1,720", "High"],
            ["/services/primary-care", "2,880", "4.6%", "1,410", "Medium"],
            ["/locations/chicago", "2,010", "6.2%", "980", "Medium"],
          ],
        },
      },
      {
        kind: "text",
        text: "**The opportunity:** the pages above pull strong organic traffic *and* absorb paid search spend at the same time. If even **10% of your $114,307/mo Google Ads budget** goes toward intent you already rank for organically, that's roughly **$38,000/month** you could shift to keywords where your organic visibility is weak — same total demand captured, lower blended cost per acquisition (CPA). The `/services/cardiology`, `/services/orthopedics`, and `/services/dermatology` pages are the place to start: each carries a strong organic conversion rate (CVR) and heavy paid volume, so they're the most likely spots to be double-paying for intent you already own.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Can you show me the full funnel from the /services landing page through to conversion?",
            "How would conversion rates change if I shifted 10% of my paid search budget from Google to Bing?",
            "What are my biggest CRO opportunities on pages with the most organic search traffic and low conversion rates?",
          ],
        },
      },
    ],
  ],
  [
    "Which channels are generating the most conversions when looking at multi-touch attribution?",
    [
      {
        kind: "text",
        text: "Under **multi-touch attribution** — where a linear model splits credit evenly across every touchpoint in a converting path — your single largest conversion driver is **Paid Search**, at **9,147** conversions over Jul 16 – Aug 15, 2026. Nearly all of that is **Google Ads (8,973)**; **Organic Search (6,240)** is your strongest non-paid channel.",
      },
      {
        kind: "chart",
        data: {
          chartType: "bar",
          title: "Conversions by channel — multi-touch (linear) attribution",
          subtitle: "Paid Search leads, and it's almost entirely Google Ads.",
          dataSource:
            "Session-level data with marketing attribution, Jul 16 – Aug 15, 2026",
          xAxisLabel: "Channel",
          yAxisLabel: "Conversions (linear credit)",
          series: [
            {
              name: "Conversions",
              data: [
                { label: "Paid Search", value: 9147 },
                { label: "Organic Search", value: 6240 },
                { label: "Direct", value: 3180 },
                { label: "Email", value: 2050 },
                { label: "Referral", value: 940 },
                { label: "Display", value: 306 },
                { label: "Paid Social", value: 8 },
              ],
            },
          ],
        },
      },
      {
        kind: "text",
        text: "Zooming into the **paid portfolio**, Google Ads isn't just leading — it's carrying nearly the whole thing. Google earns **95%** of all paid-platform conversions under linear credit, roughly **18×** the combined total of every other paid platform (**488**).",
      },
      {
        kind: "table",
        data: {
          title: "Paid advertising platforms — linear vs. last-touch",
          columns: [
            { key: "platform", label: "Platform" },
            { key: "linear", label: "Linear (multi-touch)" },
            { key: "lastTouch", label: "Last-touch" },
            { key: "share", label: "Share of paid (linear)" },
          ],
          rows: [
            ["Google Ads", "8,973", "10,965", "95%"],
            ["Tradedesk (Display)", "306", "150", "3.2%"],
            ["Bing Ads", "174", "181", "1.8%"],
            ["Meta Ads", "8", "6", "0.1%"],
          ],
        },
      },
      {
        kind: "kpis",
        data: {
          title: "Google Ads under two attribution models",
          items: [
            {
              label: "Linear (multi-touch)",
              value: "8,973",
              trend: null,
              trendDir: "neutral",
              sub: "credit split across all touchpoints",
            },
            {
              label: "Last-touch",
              value: "10,965",
              trend: "+22%",
              trendDir: "up",
              sub: "vs. linear credit",
            },
            {
              label: "Share of paid portfolio",
              value: "95%",
              trend: null,
              trendDir: "neutral",
              sub: "by linear credit",
            },
            {
              label: "Multi-channel journeys",
              value: "16%",
              trend: null,
              trendDir: "neutral",
              sub: "conversions touching 2+ channels",
            },
          ],
        },
      },
      {
        kind: "text",
        text: "The gap between the two models is the interesting part: Google Ads earns **22% more credit under last-touch (10,965) than linear (8,973)**, which tells you it's not only introducing visitors — it's frequently the *closing* touch as well. Because **16%** of conversions involve more than one channel, multi-touch attribution is the fairer lens here, and it still shows near-total Google Ads dominance of paid. Tradedesk, Bing, and Meta are marginal by comparison.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Which specific campaigns within Paid Search are driving the most last-touch conversions?",
            "What's the typical number of sessions a converting user takes before converting, and does it vary by channel?",
            "What would the conversion impact be if I redirected a portion of Paid Search traffic to /urgent-care-centers or /online-check-in?",
          ],
        },
      },
    ],
  ],
  [
    "Which platforms are assisting with conversions that aren't showing up in last-touch attribution?",
    [
      {
        kind: "text",
        text: "**Last-touch attribution only credits the final click before a conversion**, so any platform that introduces or nurtures a visitor earlier in the journey is effectively invisible in that report. The way to surface those assisting channels is to compare each platform's **last-touch** credit against its **first-touch** and **linear (multi-touch)** credit — an assisting channel earns *more* the further you move away from the last click. One platform stands out on every assist metric: **The Trade Desk**.",
      },
      {
        kind: "table",
        data: {
          title: "Advertising platforms — credit by attribution model",
          columns: [
            { key: "platform", label: "Platform" },
            { key: "lastTouch", label: "Last-touch" },
            { key: "firstTouch", label: "First-touch" },
            { key: "linear", label: "Linear (multi-touch)" },
            { key: "assist", label: "Assist rate" },
          ],
          rows: [
            ["The Trade Desk", "150", "173", "306", "52%"],
            ["Google Ads", "10,965", "9,240", "8,973", "11%"],
            ["Bing Ads", "181", "165", "174", "18%"],
            ["Meta Ads", "6", "12", "8", "40%"],
          ],
        },
      },
      {
        kind: "text",
        text: "Read the table by the direction the numbers move. **Google Ads** and **Bing Ads** earn most of their credit under last-touch — they're closers, so last-touch already sees them fully. **The Trade Desk** is the opposite: it earns **15% more under first-touch (173) than last-touch (150)** and **doubles its last-touch credit under linear (306)**. Its 52% assist rate and average journey position of **9.65** (early, awareness-stage) confirm the role — it's introducing and nurturing demand that some *other* channel then closes, so last-touch hands the credit elsewhere.",
      },
      {
        kind: "chart",
        data: {
          chartType: "bar",
          title: "The Trade Desk earns more credit the earlier you look",
          subtitle:
            "Same conversions, three attribution models. Last-touch understates it by ~2×.",
          dataSource:
            "Session-level data with marketing attribution, Jul 16 – Aug 15, 2026",
          xAxisLabel: "Attribution model",
          yAxisLabel: "Conversions credited",
          series: [
            {
              name: "The Trade Desk credit",
              data: [
                { label: "Last-touch", value: 150 },
                { label: "First-touch", value: 173 },
                { label: "Linear (multi-touch)", value: 306 },
              ],
            },
          ],
        },
      },
      {
        kind: "kpis",
        data: {
          title: "The Trade Desk across attribution models",
          items: [
            {
              label: "First-touch",
              value: "173",
              trend: "+15%",
              trendDir: "up",
              sub: "vs. last-touch (150)",
            },
            {
              label: "Last-touch",
              value: "150",
              trend: null,
              trendDir: "neutral",
              sub: "final click before convert",
            },
            {
              label: "Linear (multi-touch)",
              value: "306",
              trend: "+104%",
              trendDir: "up",
              sub: "vs. last-touch",
            },
            {
              label: "Assist rate",
              value: "52%",
              trend: null,
              trendDir: "neutral",
              sub: "avg. journey position 9.65",
            },
          ],
        },
      },
      {
        kind: "text",
        text: "The scale of what last-touch misses is the headline: The Trade Desk appears in **910 converting journeys** but drove only **11 direct clicks** — almost all of its influence is view-through, meaning the person who converted saw or watched your ad, but didn’t click on it; this is common with channels like CTV. Last-touch needs a final click to assign credit, so a channel that works this way looks roughly **half its true size** in that report. First-touch and linear are the models that actually reveal its awareness role.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Show me the conversion funnel for visitors whose journey begins via The Trade Desk",
            "How are Meta Ads assisting with conversions?",
            "Why is Direct Traffic the 2nd largest driver of conversions in multi-touch attribution?",
          ],
        },
      },
    ],
  ],
  [
    "What's my biggest conversion opportunity right now?",
    [
      {
        kind: "text",
        text: "Your biggest opportunity is the **insurance verification step** in the appointment booking funnel: 62% of visitors who reach it don't continue to confirmation. That's the largest single leak in any of your tracked funnels.",
      },
      {
        kind: "explore",
        data: {
          items: [
            {
              content:
                "**Insurance verification → Confirmation** drop-off is 62% (industry benchmark: 30–40%).",
              prompt:
                "What specific insurance verification fields are causing the most abandonment?",
            },
            {
              content:
                "**Mobile booking** converts at 2.9% vs. 5.6% on desktop — a 48% mobile penalty.",
              prompt:
                "Which steps in the mobile booking flow have the worst drop-off?",
            },
            {
              content:
                "**Cardiology service detail** has a 14% bounce rate vs. 6% site avg.",
              prompt:
                "Why does the cardiology service page have such a high bounce rate?",
            },
          ],
        },
      },
      {
        kind: "suggestions",
        data: { suggestions: PREVIEW_SUGGESTED_FOLLOWUPS },
      },
    ],
  ],
  [
    "Why is there a large drop-off in the appointment booking funnel?",
    [
      {
        kind: "text",
        text: "The appointment booking funnel loses the most users between **insurance verification** and **confirmation**. Only 38% of users who start verification continue to confirm.",
      },
      {
        kind: "funnel",
        data: {
          title: "Appointment booking funnel",
          steps: [
            {
              label: "Visit booking page",
              value: 4820,
              urlPatterns: ["/book*"],
            },
            {
              label: "Choose service",
              value: 3145,
              urlPatterns: ["/book/service*"],
            },
            {
              label: "Choose provider & time",
              value: 2410,
              urlPatterns: ["/book/provider*"],
            },
            {
              label: "Insurance verification",
              value: 1380,
              urlPatterns: ["/book/insurance*"],
            },
            {
              label: "Confirm appointment",
              value: 524,
              urlPatterns: ["/book/confirm*"],
            },
          ],
          converted: 524,
        },
      },
      {
        kind: "text",
        text: "The two highest-friction fields are **member ID** (entered incorrectly by 41% of users on first try) and **group number** (left blank by 28% of users). A field-level form-error survey would help confirm which copy or validation changes would have the biggest impact.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Create a survey to ask users about insurance form difficulty.",
            "How does mobile traffic compare to desktop in terms of conversions?",
            "Which pages should I prioritize for optimization?",
          ],
        },
      },
    ],
  ],
  [
    "Which visitor segment converts best, and why?",
    [
      {
        kind: "text",
        text: "Your highest-converting segment is **returning visitors on mobile from organic search**, at a 9.8% conversion rate — more than 2× your overall site average.",
      },
      {
        kind: "kpis",
        data: {
          title: "Top segment performance",
          items: [
            {
              label: "Segment CVR",
              value: "9.8%",
              trend: "+1.1 pts",
              trendDir: "up",
              sub: "vs. prior week",
            },
            {
              label: "Sessions",
              value: "1,240",
              trend: "+8%",
              trendDir: "up",
              sub: "vs. prior week",
            },
            {
              label: "Avg. session duration",
              value: "3m 42s",
              trend: "+22s",
              trendDir: "up",
              sub: "vs. site avg.",
            },
            {
              label: "Bounce rate",
              value: "18%",
              trend: "-4 pts",
              trendDir: "up",
              sub: "vs. site avg.",
            },
          ],
        },
      },
      {
        kind: "text",
        text: "These users typically arrive on a service-detail page (most often cardiology or orthopedics), spend 3+ minutes reading provider bios, then start the booking flow. They convert because they've already done their research before they hit the funnel.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Which pages should I prioritize for optimization?",
            "What's my biggest conversion opportunity right now?",
            "How does mobile traffic compare to desktop in terms of conversions?",
          ],
        },
      },
    ],
  ],
  [
    "Which pages should I prioritize for optimization?",
    [
      {
        kind: "text",
        text: "Three pages stand out — high traffic with conversion rates well below comparable pages on your site.",
      },
      {
        kind: "table",
        data: {
          title: "Optimization priority",
          columns: [
            { key: "path", label: "Path" },
            { key: "sessions", label: "Sessions / week" },
            { key: "cvr", label: "CVR" },
            { key: "gap", label: "Gap to peer avg" },
          ],
          rows: [
            ["/services/cardiology", 1840, "3.5%", "-2.1 pts"],
            ["/book/insurance", 1380, "38%", "-22 pts"],
            ["/locations/milwaukee", 920, "2.8%", "-2.0 pts"],
          ],
        },
      },
      {
        kind: "text",
        text: "**Why these three:** they each carry significant weekly traffic, sit on a converting path, and underperform peer pages on the same template by enough to suggest fixable UX issues rather than audience-mix differences.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Why is there a large drop-off in the appointment booking funnel?",
            "Which visitor segment converts best, and why?",
            "What's my biggest conversion opportunity right now?",
          ],
        },
      },
    ],
  ],
  [
    "How does mobile traffic compare to desktop in terms of conversions?",
    [
      {
        kind: "text",
        text: "**Mobile underperforms desktop by 48%.** Mobile sessions convert at 2.9% vs. 5.6% on desktop — and the gap has been widening for the past 3 weeks.",
      },
      {
        kind: "chart",
        data: {
          chartType: "line",
          title: "CVR over time by device",
          subtitle:
            "Mobile conversion rate has trended down while desktop has held steady.",
          dataSource: "Daily session-level conversion rate, last 30 days.",
          xAxisLabel: "Day",
          yAxisLabel: "Conversion rate (%)",
          series: [
            {
              name: "Desktop",
              data: [
                { label: "Apr 7", value: 5.4 },
                { label: "Apr 14", value: 5.6 },
                { label: "Apr 21", value: 5.5 },
                { label: "Apr 28", value: 5.7 },
                { label: "May 5", value: 5.6 },
              ],
            },
            {
              name: "Mobile",
              data: [
                { label: "Apr 7", value: 3.6 },
                { label: "Apr 14", value: 3.4 },
                { label: "Apr 21", value: 3.2 },
                { label: "Apr 28", value: 3.0 },
                { label: "May 5", value: 2.9 },
              ],
            },
          ],
        },
      },
      {
        kind: "kpis",
        data: {
          title: "Device split",
          items: [
            {
              label: "Mobile sessions",
              value: "58%",
              trend: "+3 pts",
              trendDir: "up",
              sub: "share of traffic",
            },
            {
              label: "Mobile CVR",
              value: "2.9%",
              trend: "-0.7 pts",
              trendDir: "down",
              sub: "vs. prior month",
            },
            {
              label: "Desktop CVR",
              value: "5.6%",
              trend: "+0.1 pts",
              trendDir: "neutral",
              sub: "vs. prior month",
            },
            {
              label: "Mobile booking abandons",
              value: "71%",
              trend: null,
              trendDir: "down",
              sub: "after insurance step",
            },
          ],
        },
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Why is there a large drop-off in the appointment booking funnel?",
            "Which pages should I prioritize for optimization?",
            "What's my biggest conversion opportunity right now?",
          ],
        },
      },
    ],
  ],
  [
    "Create a patient satisfaction survey for after an appointment.",
    [
      {
        kind: "text",
        text: "Here's a short post-appointment satisfaction survey you can publish. It's targeted to the appointment confirmation page so it shows up right after a booking.",
      },
      {
        kind: "survey",
        data: {
          name: "Post-appointment satisfaction",
          description:
            "A brief 4-question survey shown after appointment confirmation to measure patient experience.",
          questions: [
            {
              type: "rating",
              text: "How satisfied were you with your visit overall?",
              options: null,
              placeholder: null,
              scale: 5,
              lowLabel: "Very dissatisfied",
              highLabel: "Very satisfied",
            },
            {
              type: "single_choice",
              text: "Which part of your visit had the biggest impact on your experience?",
              options: [
                "Booking the appointment",
                "Check-in process",
                "Time spent waiting",
                "Time with the provider",
                "Follow-up & next steps",
              ],
              placeholder: null,
              scale: null,
              lowLabel: null,
              highLabel: null,
            },
            {
              type: "nps",
              text: "How likely are you to recommend us to friends or family?",
              options: null,
              placeholder: null,
              scale: null,
              lowLabel: "Not at all likely",
              highLabel: "Extremely likely",
            },
            {
              type: "open_text",
              text: "What's one thing we could do better?",
              options: null,
              placeholder: "Optional — your honest feedback helps us improve.",
              scale: null,
              lowLabel: null,
              highLabel: null,
            },
          ],
          urlMatchCondition: {
            pattern: "/book/confirm",
            matchType: "starts_with",
          },
          idleTimeSeconds: 8,
          rationale:
            "A short rating + reason + NPS + open-text combination captures both quantitative trend data and qualitative drivers without overwhelming the patient.",
          durationDays: 14,
        },
      },
    ],
  ],
  [
    "Show me recordings of people who dropped off at insurance verification.",
    [
      {
        kind: "text",
        text: "Here are three recordings of visitors who reached the **insurance verification step** in the booking funnel and didn't continue to confirmation. Across all three, the verification form is where the session stalls — useful context for any copy/validation changes you're considering on that page.",
      },
      {
        kind: "session_replay",
        data: {
          query:
            "Visitors who dropped off at /book/insurance in the last 24 hours",
          // Shared with the standalone Session Replay dashboard
          // (preview-mode/session-replay/recordings.ts) so a card opened here
          // and the same recording opened from /session-replay are identical.
          items: PREVIEW_INSURANCE_DROP_ITEMS,
          totalCount: 47,
          moreSessionsUrl:
            "/session-replay?page_url=%2Fbook%2Finsurance&not_event=Appointment_Confirmed",
        },
      },
      {
        kind: "text",
        text: "The mobile recordings (#1 and #3) both stall on the **member ID** field — the visitor types, deletes, and retypes several times before backing out. The desktop recording (#2) lingers longest on **group number** before abandoning. Pair this with a short on-step survey to confirm which copy fix would have the biggest impact.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Show me recordings of mobile users who abandoned the booking flow.",
            "Create a survey to ask users about insurance form difficulty.",
            "How does mobile traffic compare to desktop in terms of conversions?",
          ],
        },
      },
    ],
  ],
  [
    "Show me recordings of mobile users who abandoned the booking flow.",
    [
      {
        kind: "text",
        text: "Three recent mobile recordings where the visitor entered the booking funnel and abandoned before confirmation. All three highlight friction points the desktop flow doesn't surface — the mobile-only abandonment rate after the insurance step is currently 71%.",
      },
      {
        kind: "session_replay",
        data: {
          query: "Mobile visitors who abandoned /book in the last 24 hours",
          // Shared source of truth — see PREVIEW_INSURANCE_DROP_ITEMS above.
          items: PREVIEW_MOBILE_ABANDON_ITEMS,
          totalCount: 112,
          moreSessionsUrl:
            "/session-replay?device_type=mobile&page_url=%2Fbook&not_event=Appointment_Confirmed",
        },
      },
      {
        kind: "text",
        text: "The pattern across all three: visitors browse, tap into **/book**, advance one or two steps, then abandon. Recordings #1 and #3 never reach insurance verification — they stall on the service-picker. That suggests the service-line list is itself a mobile friction point, not just the insurance step.",
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Show me recordings of people who dropped off at insurance verification.",
            "Why is there a large drop-off in the appointment booking funnel?",
            "Which pages should I prioritize for optimization?",
          ],
        },
      },
    ],
  ],
  [
    "Show me recordings of visitors from Madison, WI.",
    [
      {
        kind: "text",
        text: "I searched the last 24 hours of recordings for visitors geo-located to Madison, WI and didn't find any matches. That's not unusual for a smaller market — try widening the time window or relaxing the geography filter.",
      },
      {
        kind: "session_replay",
        data: {
          query: "Visitors from Madison, WI in the last 24 hours",
          items: [],
          totalCount: 0,
          moreSessionsUrl: null,
        },
      },
      {
        kind: "suggestions",
        data: {
          suggestions: [
            "Show me recordings of people who dropped off at insurance verification.",
            "Show me recordings of mobile users who abandoned the booking flow.",
            "How does mobile traffic compare to desktop in terms of conversions?",
          ],
        },
      },
    ],
  ],
]);

export function getResponseForPrompt(input: {
  prompt: string;
  conversationKind: ConversationKind;
  envId?: string | null;
}): FakeBlock[] {
  // The HCIC booth-demo env supports ONLY Kylie's canned prompts — never the
  // Meridian responses or the kind-based transcripts below.
  if (isHcicDemoEnv(input.envId)) {
    return getHcicResponseForPrompt(input.prompt);
  }
  if (input.conversationKind === "weekly_summary") return WEEKLY_SUMMARY_BLOCKS;
  if (input.conversationKind === "funnel_dig_deeper")
    return FUNNEL_DIG_DEEPER_BLOCKS;
  if (input.conversationKind === "session_replay_recording_analysis")
    return SESSION_REPLAY_RECORDING_ANALYSIS_BLOCKS;
  if (input.conversationKind === "session_replay_list_analysis")
    return SESSION_REPLAY_LIST_ANALYSIS_BLOCKS;
  const matched = RESPONSES_BY_PROMPT.get(input.prompt.trim());
  if (matched) return matched;
  return [
    { kind: "text", text: PREVIEW_DISCLAIMER },
    {
      kind: "suggestions",
      data: { suggestions: PREVIEW_SUGGESTED_FOLLOWUPS },
    },
  ];
}

// Translates simulator-shaped blocks (the wire shape emitted on `block`
// events) into the Anthropic content-block shape that gets persisted to
// the store. Symmetric with `extractAssistantBlocks` in
// useConversationStream.ts so a persisted message replays faithfully.
// Lives here (not in simulator.ts) so the store's seeded historical
// fixtures can reuse the same transform without dragging the simulator's
// SSE plumbing in.
export function blocksToStoredContent(
  blocks: FakeBlock[],
): Array<Record<string, unknown>> {
  return blocks.map((b) => {
    if (b.kind === "text") {
      return { type: "text", text: b.text };
    }
    return { type: "tool_use", name: `emit_${b.kind}`, input: b.data };
  });
}

// Mirrors back/app/aichat/conversation_handler.go's
// `kind.HidesFirstUserMessage()` — the set of app-generated kinds whose
// synthetic first user prompt should be hidden from the chat history.
// Centralized here so simulator.ts (runtime visibility) and store.ts
// (seeded historical fixture) can't drift if the backend set grows.
export function kindHidesFirstUserMessage(kind: ConversationKind): boolean {
  return (
    kind === "weekly_summary" ||
    kind === "funnel_dig_deeper" ||
    kind === "session_replay_recording_analysis" ||
    kind === "session_replay_list_analysis" ||
    kind === "heatmap_page_analysis"
  );
}
